import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex } from '../../../styled-system/jsx';
import { catchError } from '../../errors/utils/handleError';
import { getAuth } from '../../firebase';
import { H2 } from '../../ui/font/headings/components/H2';
import { Button } from '../../ui/form/buttons/button/component/Button';
import { LoadingModal } from '../../ui/loader/components/LoadingModal';
import { useAuth } from '../hooks/useAuth';

const RECHECK_MS = 60 * 1000;

const provider = new GoogleAuthProvider();

export const Login = () => {
  const firebaseAuth = getAuth();
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSignIn = useCallback(async () => {
    try {
      setLoading(true);
      await signInWithPopup(firebaseAuth, provider);
      setLoading(false);
    } catch (err) {
      catchError(err);
    }
  }, [firebaseAuth]);

  const handleSignInSync = useCallback(() => {
    void handleSignIn();
  }, [handleSignIn]);

  useEffect(() => {
    if (auth.uid && auth.isAdmin) {
      navigate('/app/collections');
    }
  }, [navigate, auth.isAdmin, auth.uid]);

  const [nextCheck, setNextCheck] = useState(() => ({ at: 0, diff: 0 }));

  const handleRefresh = useCallback(() => {
    void auth.refresh().finally(() => {
      setNextCheck({ at: Date.now() + RECHECK_MS, diff: RECHECK_MS });
    });
  }, [auth]);

  useEffect(() => {
    if (!auth.uid) {
      return;
    }

    if (!auth.isAdmin) {
      if (!nextCheck.at) {
        setNextCheck({ at: Date.now() + RECHECK_MS, diff: RECHECK_MS });
      }
    }

    const updateTimer = () => {
      if (nextCheck.diff <= 0) {
        handleRefresh();
      } else {
        setNextCheck((oldCheck) => ({
          at: oldCheck.at,
          diff: Math.max(oldCheck.at - Date.now(), 0),
        }));
      }
    };

    const intervalId = setInterval(updateTimer, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [auth.isAdmin, auth.uid, handleRefresh, nextCheck.at, nextCheck.diff]);

  return (
    <div>
      {loading ? (
        <LoadingModal />
      ) : !auth.uid ? (
        <Flex flexDir="column" justifyContent="space-evenly" height="52">
          <H2>Welcome to the Kaddo Foundry!</H2>
          <Flex justifyContent="center">
            <Button onClick={handleSignInSync}>Sign In</Button>
          </Flex>
        </Flex>
      ) : !auth.isAdmin ? (
        <Flex flexDir="column" justifyContent="space-evenly" height="52">
          <H2>Waiting for admin approval...</H2>
          <div>
            You need to be approved in order to be a Kaddo Foundry admin. Next
            check in {Math.floor(nextCheck.diff / 1000)} seconds
          </div>
          <Button onClick={handleRefresh} css={{ alignSelf: 'center' }}>
            Refresh
          </Button>
        </Flex>
      ) : (
        <div>redirecting...</div>
      )}
    </div>
  );
};
